import { forwardRef, type ReactNode } from 'react';

import { Close } from '@air/next-icons';
import { Button, type ButtonProps } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';

import { type IconType } from '~/types/icons';

const MAX_VALUES_TO_SHOW = 3;

export type FilterButtonProps = {
  title: string | ReactNode;
  Icon?: IconType;
  isOpen?: boolean;
  count?: number;
  values?: string[];
  onRemoveClick?: () => void;
} & Pick<ButtonProps, 'onClick'>;

export const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(
  ({ count, Icon, isOpen, onRemoveClick, title, values, ...restOfProps }: FilterButtonProps, ref) => {
    const hasCloseButton = !!onRemoveClick && (!!count || !!values?.length);

    const isActive = isOpen || !!values?.length || !!count;
    const isTruncated = (!!values && values.length > MAX_VALUES_TO_SHOW) || (!values && !!count);

    return (
      <div
        className="relative shrink-0"
        data-testid="FILTER_BUTTON_CONTAINER"
        data-title={title}
        data-truncated={isTruncated}
      >
        <Button
          appearance="outline"
          className={hasCloseButton ? 'pr-5' : 'pr-2'}
          color={isActive ? 'blue' : 'grey'}
          data-testid="FILTER_BUTTON"
          data-title={title}
          prefix={Icon && <Icon className="size-3 shrink-0" />}
          suffix={null}
          size="small"
          ref={ref}
          {...restOfProps}
        >
          <div className="flex items-center gap-1">
            {isTruncated && (count || values?.length) && (
              <span data-testid="FILTER_BUTTON_COUNT">{count || values?.length}</span>
            )}
            {values?.length && !isTruncated ? values.join(', ') : title}
          </div>
        </Button>
        {hasCloseButton && (
          <IconButton
            appearance="ghost"
            className="absolute top-1/2 right-1 -translate-y-1/2"
            color={isActive ? 'blue' : 'grey'}
            data-testid="FILTER_BUTTON_CLOSE"
            icon={Close}
            label="Clear filters"
            onClick={onRemoveClick}
            size="extra-small"
          />
        )}
      </div>
    );
  },
);

FilterButton.displayName = 'FilterButton';
